import * as Sentry from '@sentry/react';
import uk from 'date-fns/locale/uk';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import localeData from 'dayjs/plugin/localeData';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import weekday from 'dayjs/plugin/weekday';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import weekYear from 'dayjs/plugin/weekYear';
import { useEffect } from 'react';
import { registerLocale, setDefaultLocale } from 'react-datepicker';
import { createRoot } from 'react-dom/client';
import { createRoutesFromChildren, matchRoutes, RouterProvider, useLocation, useNavigationType } from 'react-router';

import 'dayjs/locale/uk';

import RootLayout from '@/router/RootLayout';

import { config } from './config';
import { emit } from './eventBus';
import { watchDuplicateStylesheets } from './mf/dedupeStylesheets';
import loadGoogleAnalytics from './loader/googleTagManager';

// eslint-disable-next-line import/no-unresolved
import { router } from '#router';

function initApp(): void {
  // eslint-disable-next-line no-console
  console.info('VERSION: ', config.VERSION);
  console.debug('RELEASE: ', config.RELEASE);

  const rootElement = document.getElementById('root');
  if (!rootElement) throw new Error('Failed to find the root element');
  const root = createRoot(rootElement);

  window.addEventListener('popstate', () => {
    emit({
      action: 'modal_force_close',
    });
  });

  loadGoogleAnalytics(config.VERSION, config.googleTagManager);

  registerLocale('uk', uk);
  setDefaultLocale('uk');
  dayjs.locale('uk');
  dayjs.extend(customParseFormat);
  dayjs.extend(advancedFormat);
  dayjs.extend(weekday);
  dayjs.extend(localeData);
  dayjs.extend(weekOfYear);
  dayjs.extend(weekYear);
  dayjs.extend(utc);
  dayjs.extend(timezone);
  dayjs.extend(isSameOrAfter);
  dayjs.extend(isSameOrBefore);
  dayjs.tz.setDefault('GMT');

  Sentry.init({
    ...config.sentry,
    integrations: [
      // See docs for support of different versions of variation of react router
      // https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router'/
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration(),
    ],
  });

  root.render(
    <RootLayout>
      <RouterProvider router={router} />
    </RootLayout>,
  );
}

watchDuplicateStylesheets();
initApp();
